import * as React from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WorkspaceNotice } from '@/components/ui/workspace';
import { citedDocumentFromError, useDeleteDocumentMutation } from '@/lib/api/documents';

/**
 * The Delete control of the document detail screen, with its two-step
 * confirm, and the refusal it shows when the document is cited (T-0152).
 *
 * E11 rule 26: a document that an engagement requirement cites cannot be
 * deleted. The server refuses with a `conflict` whose details name the
 * citing requirements; this shows that as an alert saying how many
 * requirements cite the document, the first few numbers, and where to
 * find them. Nothing is deleted and the screen stays where it is.
 *
 * The link to the Requirements screen is rendered by the caller (the
 * route owns the router), which keeps this component free of one.
 */

/** How many citing requirement numbers the alert spells out. */
const NUMBERS_SHOWN = 5;

export interface DeleteDocumentControlProps {
  engagementId: string;
  documentId: string;
  /** Called after the document has been deleted. */
  onDeleted: () => void;
  /** Renders the link to the engagement's Requirements screen around `label`. */
  renderRequirementsLink: (label: string) => React.ReactNode;
}

export function DeleteDocumentControl({
  engagementId,
  documentId,
  onDeleted,
  renderRequirementsLink,
}: DeleteDocumentControlProps) {
  const [confirming, setConfirming] = React.useState(false);
  const mutation = useDeleteDocumentMutation(engagementId);
  const cited = mutation.isError ? citedDocumentFromError(mutation.error) : null;
  const otherError =
    mutation.isError && !cited
      ? mutation.error instanceof Error && mutation.error.message
        ? mutation.error.message
        : 'The document could not be deleted.'
      : null;

  const shown = cited ? cited.requirementNumbers.slice(0, NUMBERS_SHOWN) : [];
  const more = cited ? cited.total - shown.length : 0;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={mutation.isPending}
        onClick={() => {
          if (!confirming) {
            mutation.reset();
            setConfirming(true);
            return;
          }
          mutation.mutate(documentId, {
            onSuccess: onDeleted,
            // Refused: back to the first step, with the reason shown below.
            onError: () => setConfirming(false),
          });
        }}
        className={confirming ? 'border-cite-broken text-cite-broken' : undefined}
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
        <span>{mutation.isPending ? 'Deleting…' : confirming ? 'Confirm delete' : 'Delete'}</span>
      </Button>

      {cited ? (
        <WorkspaceNotice tone="critical" className="basis-full">
          <strong className="font-semibold text-ink-900">
            This document cannot be deleted: {cited.total.toLocaleString()}{' '}
            {cited.total === 1 ? 'requirement cites' : 'requirements cite'} it.
          </strong>{' '}
          {shown.length > 0 ? (
            <>
              <span className="font-mono text-meta">{shown.join(', ')}</span>
              {more > 0 ? ` and ${more.toLocaleString()} more` : ''}.{' '}
            </>
          ) : null}
          A document stays while a requirement cites it, including requirements that have been
          removed, because their citations are part of the record. Nothing was deleted.{' '}
          {renderRequirementsLink('Open Requirements')}
        </WorkspaceNotice>
      ) : null}
      {otherError ? (
        <WorkspaceNotice tone="critical" className="basis-full">
          {otherError}
        </WorkspaceNotice>
      ) : null}
    </>
  );
}
